import React, { useState, useEffect } from 'react';
import { Bell, ShoppingCart, ArrowLeft, Search, Heart } from 'lucide-react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../../contexts/CartContext';
// LogoIcon import removed
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db, auth } from '../../lib/firebase';

interface MobileHeaderProps {
    title?: string;
    showBack?: boolean;
    transparent?: boolean;
    darkIcons?: boolean;
    rightElement?: React.ReactNode;
    onSearchClick?: () => void;
    showLogo?: boolean;
    logoLayoutId?: string;
}

const MobileHeader: React.FC<MobileHeaderProps> = ({
    title,
    showBack = false,
    transparent = false,
    darkIcons = false,
    rightElement,
    onSearchClick,
    showLogo = false,
    logoLayoutId
}) => {
    const navigate = useNavigate();
    const { items } = useCart();
    const cartCount = Object.keys(items).length;

    // Unread Notification Count
    const [unreadCount, setUnreadCount] = useState(0);

    // Only listen for notifications if this is the main header (not internal page usage like transparent)
    // or we can just always listen, it's cheap.
    useEffect(() => {
        if (!auth.currentUser) return;
        const q = query(
            collection(db, 'notifications'),
            where('userId', '==', auth.currentUser.uid),
            where('isRead', '==', false)
        );
        const unsub = onSnapshot(q, (snap) => {
            setUnreadCount(snap.size);
        });
        return () => unsub();
    }, []);

    // Style logic
    const bgClass = transparent
        ? 'bg-transparent border-transparent'
        : 'bg-brand-white/70 dark:bg-brand-white/10 backdrop-blur-2xl border-b border-brand-accent/5 dark:border-white/5 shadow-sm';

    // Icon colors - Explicitly white in dark mode for maximum visibility
    const iconColorClass = transparent && !darkIcons ? 'text-white' : 'text-brand-clay dark:text-white';
    const secondaryIconClass = transparent && !darkIcons ? 'text-white/60' : 'text-brand-clay-light dark:text-white/70';

    return (
        <header className={`fixed top-0 left-0 right-0 z-50 h-[var(--safe-top)] min-h-[56px] flex items-center justify-between px-4 transition-all duration-300 ${bgClass} selection:bg-transparent`}>

            {/* Left Action: Logo or Back Button */}
            <div className="flex items-center gap-3">
                {showBack ? (
                    <button
                        onClick={() => navigate(-1)}
                        className={`p-2 -ml-2 rounded-full active:scale-90 transition-transform ${transparent ? 'bg-black/20 backdrop-blur-md text-white' : 'hover:bg-brand-clay/5 text-brand-clay'}`}
                    >
                        <ArrowLeft size={24} strokeWidth={2} className={transparent ? 'text-white' : iconColorClass} />
                    </button>
                ) : (
                    <div
                        onClick={() => {
                            const isPaymentPage = window.location.pathname.includes('/deposit') ||
                                window.location.pathname.includes('/wallet/withdraw') ||
                                window.location.pathname.includes('/checkout');

                            if (!isPaymentPage) {
                                // Trigger animation on click by forcing re-render
                                const el = document.getElementById('fct-star-arc');
                                if (el) {
                                    el.classList.remove('animate');
                                    void el.offsetWidth; // Force reflow
                                    el.classList.add('animate');
                                }
                                navigate('/');
                            }
                        }}
                        className="relative flex items-center cursor-pointer group select-none py-1 px-1"
                    >
                        {/* THE BRAND TEXT */}
                        <div className="flex items-center text-xl md:text-2xl font-black italic tracking-tighter relative z-10">
                            <span className="text-brand-clay dark:text-white transition-colors">
                                FASTCASH
                            </span>
                            <span className="text-brand-clay/40 dark:text-white/40 transition-colors ml-0.5">
                                TRADE
                            </span>
                        </div>

                        {/* THE DISNEY ARC - SVG Animation */}
                        <svg
                            id="fct-star-arc"
                            className="absolute inset-0 w-full h-full pointer-events-none overflow-visible animate"
                            viewBox="0 0 200 50"
                            preserveAspectRatio="xMidYMid meet"
                            style={{ transform: 'scale(1.2)', transformOrigin: 'center' }}
                        >
                            <defs>
                                {/* Arc Gradient - Light Mode: Warm Gold, Dark Mode: White-Gold */}
                                <linearGradient id="headerArcGlow" x1="0%" y1="0%" x2="100%" y2="0%">
                                    <stop offset="0%" stopColor="transparent" />
                                    <stop offset="30%" className="[stop-color:#B8860B] dark:[stop-color:#FDFDFD]" stopOpacity="0.6" />
                                    <stop offset="50%" stopColor="#D4AF37" stopOpacity="1" />
                                    <stop offset="70%" className="[stop-color:#B8860B] dark:[stop-color:#FDFDFD]" stopOpacity="0.6" />
                                    <stop offset="100%" stopColor="transparent" />
                                </linearGradient>
                                {/* Glow Filter */}
                                <filter id="headerStarGlow" x="-100%" y="-100%" width="300%" height="300%">
                                    <feGaussianBlur in="SourceGraphic" stdDeviation="1.5" result="blur" />
                                    <feMerge>
                                        <feMergeNode in="blur" />
                                        <feMergeNode in="SourceGraphic" />
                                    </feMerge>
                                </filter>
                                {/* Drop Shadow for Light Mode */}
                                <filter id="headerStarShadow" x="-50%" y="-50%" width="200%" height="200%">
                                    <feDropShadow dx="0" dy="1" stdDeviation="2" floodColor="#8B6914" floodOpacity="0.4" />
                                </filter>
                            </defs>

                            {/* The Arc Path - Curves from left-bottom, up over center, down to right-bottom */}
                            <motion.path
                                d="M 10 38 Q 100 -5 190 38"
                                fill="transparent"
                                stroke="url(#headerArcGlow)"
                                strokeWidth="1.2"
                                strokeLinecap="round"
                                filter="url(#headerStarGlow)"
                                initial={{ pathLength: 0, opacity: 0 }}
                                animate={{ pathLength: [0, 1], opacity: [0, 0.8, 0] }}
                                transition={{
                                    duration: 1.5,
                                    ease: [0.16, 1, 0.3, 1],
                                    delay: 0.5,
                                    repeat: Infinity,
                                    repeatDelay: 5
                                }}
                            />

                            {/* The Star Head - Follows the arc path */}
                            <motion.circle
                                r="3"
                                className="fill-[#D4AF37] dark:fill-[#FDFDFD]"
                                filter="url(#headerStarGlow)"
                                initial={{ opacity: 0, cx: 10, cy: 38 }}
                                animate={{
                                    opacity: [0, 1, 1, 0],
                                    cx: [10, 100, 190],
                                    cy: [38, 5, 38]
                                }}
                                transition={{
                                    duration: 1.5,
                                    ease: [0.16, 1, 0.3, 1],
                                    delay: 0.5,
                                    repeat: Infinity,
                                    repeatDelay: 5,
                                    times: [0, 0.5, 1]
                                }}
                                style={{
                                    filter: 'drop-shadow(0 0 6px #D4AF37) drop-shadow(0 0 12px #B8860B)'
                                }}
                            />

                            {/* Sparkle Particles - Left behind by the star */}
                            {[0, 1, 2].map((i) => (
                                <motion.circle
                                    key={i}
                                    r="0.8"
                                    fill="#D4AF37"
                                    initial={{ opacity: 0, cx: 50 + i * 50, cy: 20 - i * 5 }}
                                    animate={{
                                        opacity: [0, 0.8, 0],
                                        cy: [20 - i * 5, 15 - i * 5]
                                    }}
                                    transition={{
                                        duration: 0.8,
                                        delay: 0.8 + i * 0.15,
                                        repeat: Infinity,
                                        repeatDelay: 5.7 - i * 0.15,
                                        ease: "easeOut"
                                    }}
                                />
                            ))}
                        </svg>
                    </div>
                )}
            </div>

            {/* Title: Only show if explicit title provided AND (Back mode OR transparent scrolled mode) */}
            {title && (showBack || (transparent && darkIcons)) && (
                <div className={`absolute left-1/2 -translate-x-1/2 font-bold text-lg tracking-tight ${iconColorClass}`}>
                    {title}
                </div>
            )}

            {/* Right Actions */}
            <div className="flex items-center gap-4">
                {rightElement ? (
                    rightElement
                ) : (
                    <>
                        <button
                            onClick={() => navigate('/search')}
                            className={`p-2 rounded-full active:scale-90 transition-transform ${transparent ? 'bg-black/20 backdrop-blur-md text-white' : `hover:bg-brand-clay/5 ${secondaryIconClass}`}`}
                        >
                            <Search size={22} strokeWidth={2} className={transparent ? 'text-white' : secondaryIconClass} />
                        </button>

                        <button
                            onClick={() => navigate('/favorites')}
                            className={`p-2 rounded-full active:scale-90 transition-transform ${transparent ? 'bg-black/20 backdrop-blur-md text-white' : `hover:bg-brand-clay/5 ${secondaryIconClass}`}`}
                        >
                            <Heart size={22} strokeWidth={2} className={transparent ? 'text-white' : secondaryIconClass} />
                        </button>

                        <button
                            onClick={() => navigate('/notifications')}
                            className={`relative active:scale-90 transition-transform ${transparent ? 'bg-black/20 backdrop-blur-md p-2 rounded-full text-white' : `p-2 hover:bg-brand-clay/5 rounded-full ${secondaryIconClass}`}`}
                        >
                            {/* Ensured explicit color class for deep visibility */}
                            <Bell size={24} strokeWidth={1.5} className={transparent ? 'text-white' : secondaryIconClass} />
                            {unreadCount > 0 && (
                                <span className="absolute top-1.5 right-1.5 w-2.5 h-2.5 bg-brand-accent rounded-full border-2 border-brand-white shadow-[0_0_8px_rgba(212,175,55,0.5)]" />
                            )}
                        </button>

                        <button
                            onClick={() => navigate('/cart')}
                            className={`relative active:scale-90 transition-transform ${transparent ? 'bg-black/20 backdrop-blur-md p-2 rounded-full text-white' : `p-2 hover:bg-brand-clay/5 rounded-full ${secondaryIconClass}`}`}
                        >
                            {/* Ensured explicit color class for deep visibility */}
                            <ShoppingCart size={24} strokeWidth={1.5} className={transparent ? 'text-white' : secondaryIconClass} />
                            {cartCount > 0 && (
                                <span className="absolute -top-1 -right-1 bg-brand-accent text-white text-[9px] font-bold flex items-center justify-center rounded-full w-4 h-4 shadow-[0_2px_8px_rgba(212,175,55,0.4)] border border-brand-white">
                                    {cartCount}
                                </span>
                            )}
                        </button>
                    </>
                )}
            </div>
        </header>
    );
};

export default MobileHeader;
